import clsx from "classnames";
import { useId } from "react";
import { Container } from "react-bootstrap";
import FAQIntro from "./components/FAQIntro";
import FAQTitleBar from "./components/FAQTitleBar";
import FAQItem from "./components/FAQItem";
import FAQSchema from "./components/FAQSchema";
import classes from "./styles.module.scss";

/**
 * FAQ section: an optional heading and intro, a coloured title bar, and a card of expandable
 * question/answer items. The same items feed the FAQPage structured data, so the visible copy and
 * the schema can never drift apart.
 *
 * @param {string} [className] - Additional classes for the root element (spacing).
 * @param {string} [bgColor] - Background colour class for the section.
 * @param {string} [title] - Heading shown above the title bar.
 * @param {string} [titleColor] - Text colour class for the heading.
 * @param {string} [description] - Intro rich text under the heading.
 * @param {string} [textColor] - Text colour class for the intro copy.
 * @param {string} [introAlignment] - Text alignment class for the heading and intro copy.
 * @param {boolean} [showTitleBar] - Whether the coloured title bar is rendered.
 * @param {string} [titleBarTitle] - Heading inside the title bar.
 * @param {string} [titleBarTag] - HTML tag for the title bar heading.
 * @param {string} [titleBarColor] - Fill key for the title bar.
 * @param {Array} [items] - FAQ items; each item holds question, answer and open.
 * @param {string} [questionColor] - Fill key for the question rows.
 * @param {boolean} [singleOpen] - Close other items when one is opened.
 * @param {boolean} [enableSchema] - Whether FAQPage structured data is output.
 * @returns {JSX.Element}
 */
export const FAQ = ({
  className = "py-5",
  bgColor = "bg-white",
  title,
  titleColor = "text-primary",
  description,
  textColor = "text-body",
  introAlignment = "text-start",
  showTitleBar = true,
  titleBarTitle,
  titleBarTag = "h2",
  titleBarColor = "gradient",
  items = [],
  questionColor = "navy",
  singleOpen = false,
  enableSchema = true,
}) => {
  const id = useId();
  const visibleItems = items?.filter((item) => !!item?.question) ?? [];
  const groupName = singleOpen ? `faq-${id}` : undefined;

  return (
    <div data-component="FAQ" className={clsx(className, bgColor, classes.faq)}>
      <Container>
        <FAQIntro
          title={title}
          titleColor={titleColor}
          description={description}
          textColor={textColor}
          alignment={introAlignment}
        />

        <FAQTitleBar show={showTitleBar} title={titleBarTitle} tag={titleBarTag} color={titleBarColor} />

        {!!visibleItems.length && (
          <div className={classes.faq__card} data-sourceflow-prop="items">
            {visibleItems.map((item, index) => (
              <FAQItem
                key={item?.id ?? index}
                question={item.question}
                answer={item.answer}
                open={item.open}
                name={groupName}
                color={questionColor}
              />
            ))}
          </div>
        )}
      </Container>

      {enableSchema && <FAQSchema items={visibleItems} />}
    </div>
  );
};

export default FAQ;
